import { localizePhoneNumbers } from '@lucid/gatsby-theme-lucid';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';

import Link from '../link/link';
import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_ParagraphChartHeaderSection,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphOrCallCta,
  EnterpriseHeaderLogosJsonEdge
} from '../../generated/graphql';
import './chart-header.scss';
import useMarketoLink from '../../hooks/use-marketo-link';
import { Heading } from 'lucid-components-react';

interface ChartHeaderProps {
  data: Drupal_ParagraphChartHeaderSection;
  logos: EnterpriseHeaderLogosJsonEdge[];
}

const ChartHeader = ({ data, logos }: ChartHeaderProps) => {
  const {
    fieldTitle,
    fieldTitlePiece,
    fieldSubtitle,
    fieldCtaButtons,
    fieldDescription,
    fieldLogosUnder,
    fieldPhoneCta,
    fieldShowLogos,
    fieldVideoLink
  } = data;
  const { language, video } = useContext(LayoutContext);
  const phoneCtaEntity = fieldPhoneCta
    ? (fieldPhoneCta.entity as Drupal_ParagraphOrCallCta)
    : null;

  const getMarketoLink = useMarketoLink(language);

  const fullTitle = fieldTitlePiece?.length
    ? fieldTitlePiece.filter(piece => piece?.trim()).join('<br>')
    : fieldTitle;

  return (
    <header className={`chart-header ${!fieldTitle && 'no-title'}`}>
      {fullTitle && (
        <>
          <Heading
            variant="headlineXS"
            color="white"
            css={{
              maxWidth: '700px',
              textAlign: 'center',
              margin: '40px auto 0'
            }}
          >
            {ReactHtmlParser(fullTitle)}
          </Heading>

          {fieldSubtitle && (
            <Heading
              as="h3"
              variant="subheadS"
              color="white"
              css={{
                maxWidth: '700px',
                textAlign: 'center',
                margin: '20px auto 30px'
              }}
            >
              {fieldSubtitle}
            </Heading>
          )}

          {fieldVideoLink && (
            <button
              className="chart-header__video-button"
              type="button"
              onClick={() => video.getVideoModal(fieldVideoLink)}
            />
          )}

          {fieldShowLogos && logos.length > 0 && (
            <div className={`logos${fieldLogosUnder ? ' logos--below' : ''}`}>
              {logos.map(logo => {
                const image = logo.node;
                return (
                  <div key={image.alt!}>
                    <img
                      className={`logos__logo${
                        image.url!.includes('.svg') ? ' logos__logo--svg' : ''
                      }`}
                      src={image.url!}
                      alt={image.alt!}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {fieldDescription && <p>{fieldDescription}</p>}

          <div className="chart-header__ctas">
            {fieldCtaButtons!.map(cta => {
              const button = cta!.entity as Drupal_ParagraphCtaButton;
              const { url, copy } = getMarketoLink(
                (button?.fieldMarketo?.entity as Drupal_ParagraphMarketoForm)
                  ?.fieldMarketoId
              )!;

              return button.fieldButtonLink ? (
                <Link
                  key={button.fieldButtonText!}
                  to={button.fieldButtonLink}
                  primary={!button.fieldIsSecondary}
                  secondary={!!button.fieldIsSecondary}
                  asButton
                >
                  {button.fieldButtonText}
                </Link>
              ) : (
                <Link
                  to={url}
                  asButton
                  key={button.fieldButtonText!}
                  primary={!button.fieldIsSecondary}
                  secondary={!!button.fieldIsSecondary}
                >
                  {button.fieldButtonText || copy}
                </Link>
              );
            })}
            {phoneCtaEntity && (
              <div className="chart-header__phone">
                <Link
                  to={localizePhoneNumbers(
                    `tel:${phoneCtaEntity.fieldCallCtaPhoneNumber}`
                  )}
                >
                  {phoneCtaEntity.fieldCallCtaText}
                  &nbsp;
                  {localizePhoneNumbers(
                    phoneCtaEntity.fieldCallCtaPhoneNumber!
                  )}
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
};

export default ChartHeader;
